import React from 'react';
import { View, Text, ScrollView, Platform, StyleSheet } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Svg, Path, Rect, Circle } from 'react-native-svg';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import { useLanguage } from '@/contexts/LanguageContext';

const isIOS = Platform.OS === 'ios';

function WidgetIcon() {
  return (
    <Svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <Rect width="48" height="48" rx="12" fill="#F0EEE4" />
      <Rect x="10" y="10" width="28" height="28" rx="6" stroke="#151515" strokeWidth="2" />
      <Path
        d="M24 16v8l4 4"
        stroke="#669AB7"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Circle cx="24" cy="24" r="2" fill="#015480" />
    </Svg>
  );
}

function StepIcon({ step }: { step: number }) {
  return (
    <View style={styles.stepBadge}>
      <Text style={styles.stepBadgeText}>{step}</Text>
    </View>
  );
}

interface StepCardProps {
  step: number;
  description: string;
}

function StepCard({ step, description }: StepCardProps) {
  return (
    <View style={styles.stepCard}>
      <StepIcon step={step} />
      <Text style={styles.stepText}>{description}</Text>
    </View>
  );
}

export default function WidgetGuideScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();

  const iosSteps = [
    t('widget.guide.ios.step1'),
    t('widget.guide.ios.step2'),
    t('widget.guide.ios.step3'),
    t('widget.guide.ios.step4'),
    t('widget.guide.ios.step5'),
  ];

  const androidSteps = [
    t('widget.guide.android.step1'),
    t('widget.guide.android.step2'),
    t('widget.guide.android.step3'),
    t('widget.guide.android.step4'),
  ];

  const steps = isIOS ? iosSteps : androidSteps;

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader
        title={t('widget.guide.title')}
        onBack={() => router.back()}
        showBackButton
      />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.iconRow}>
          <WidgetIcon />
        </View>

        <Text style={styles.subtitle}>{t('widget.guide.subtitle')}</Text>

        <View style={styles.stepsContainer}>
          {steps.map((step, index) => (
            <StepCard key={index} step={index + 1} description={step} />
          ))}
        </View>
      </ScrollView>

      <View style={[styles.footer, { paddingBottom: insets.bottom + 24 }]}>
        <PrimaryButton
          title={t('widget.guide.done')}
          onPress={() => router.back()}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    gap: 24,
    flexDirection: 'column',
  },
  iconRow: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  subtitle: {
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
    color: '#151515',
    textAlign: 'center',
    paddingHorizontal: 8,
  },
  stepsContainer: {
    gap: 12,
    flexDirection: 'column',
    width: '100%',
  },
  stepCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    backgroundColor: '#F9F8F3',
    borderWidth: 1,
    borderColor: '#F0EEE4',
    borderRadius: 12,
    padding: 16,
  },
  stepBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#151515',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  stepBadgeText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 14,
  },
  stepText: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 21,
    color: '#151515',
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
  },
});
